import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router";
import { api } from "../api/client";
import { TotemHead, TotemSegment } from "../components/Totem";
import styles from "./Archive.module.css";

interface ArchivedTotem {
  id: string;
  question: string;
  period_end: string;
  total_paid_amount_cents: number;
  has_winner: boolean;
}

interface TotemDetail {
  id: string;
  question: string;
  total_paid_amount_cents: number;
  total_likes_count: number;
  total_answers_count: number;
  winner_answer_id: string | null;
  winner_answer_content: string | null;
  period_end: string;
}

interface Answer {
  answer_id: string;
  content: string;
  created_at: string;
  like_count?: number;
}

export function Archive() {
  const { id } = useParams<{ id: string }>();
  const [totems, setTotems] = useState<ArchivedTotem[]>([]);
  const [selectedTotem, setSelectedTotem] = useState<TotemDetail | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [currentAnswerIndex, setCurrentAnswerIndex] = useState(0);
  const [isBrowsing, setIsBrowsing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const [allAnswersBrowsed, setAllAnswersBrowsed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(300);

  const isLoadingRef = useRef(false);
  const lastScrollTime = useRef(0);
  const headerRef = useRef<HTMLDivElement>(null);

  // Measure header height
  useEffect(() => {
    if (headerRef.current) {
      const updateHeight = () => {
        if (headerRef.current) {
          setHeaderHeight(headerRef.current.offsetHeight);
        }
      };
      updateHeight();
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }
  }, [selectedTotem, isBrowsing]);

  // Fetch list of archived totems
  useEffect(() => {
    async function fetchTotems() {
      try {
        const data = await api.get<{ totems: ArchivedTotem[] }>("/totems");
        setTotems(data.totems);
      } catch (err) {
        console.error("Failed to fetch totems:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchTotems();
  }, []);

  // Fetch selected totem details
  useEffect(() => {
    if (!id) {
      setSelectedTotem(null);
      setAnswers([]);
      setCurrentAnswerIndex(0);
      setIsBrowsing(false);
      setAllAnswersBrowsed(false);
      return;
    }

    async function fetchTotem() {
      setLoading(true);
      try {
        const data = await api.get<TotemDetail>(`/totem/${id}`);
        setSelectedTotem(data);

        // Reset browsing on totem change
        await api.post("/session/reset-browsing");
      } catch (err) {
        console.error("Failed to fetch totem:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchTotem();
  }, [id]);

  // Fetch random answer from archive (excluding winner)
  const fetchRandomAnswer = useCallback(async () => {
    if (!selectedTotem || isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoadingAnswers(true);
    try {
      const response = await api.get<
        Answer | { error: string; all_viewed?: boolean }
      >(`/totem/${selectedTotem.id}/random`);

      if ("error" in response) {
        if (response.all_viewed) {
          setAllAnswersBrowsed(true);
        }
      } else {
        const answer = response as Answer;
        // Skip winner answer (it's displayed separately)
        if (answer.answer_id === selectedTotem.winner_answer_id) {
          // Fetch another one
          isLoadingRef.current = false;
          setLoadingAnswers(false);
          fetchRandomAnswer();
          return;
        }

        if (!answers.find((a) => a.answer_id === answer.answer_id)) {
          setAnswers((prev) => {
            const newAnswers = [...prev, answer];
            setCurrentAnswerIndex(newAnswers.length - 1);
            return newAnswers;
          });
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.message.includes("All answers viewed")) {
        setAllAnswersBrowsed(true);
      } else {
        console.error("Failed to fetch answer:", err);
      }
    } finally {
      setLoadingAnswers(false);
      isLoadingRef.current = false;
    }
  }, [selectedTotem, answers]);

  // Start browsing
  const startBrowsing = useCallback(() => {
    if (!selectedTotem || selectedTotem.total_answers_count === 0) return;
    if (!isBrowsing) {
      setIsBrowsing(true);
      fetchRandomAnswer();
    }
  }, [isBrowsing, fetchRandomAnswer, selectedTotem]);

  // Navigate to next answer or fetch new one
  const goToNextAnswer = useCallback(() => {
    if (!isBrowsing) {
      startBrowsing();
      return;
    }
    if (currentAnswerIndex < answers.length - 1) {
      setCurrentAnswerIndex((prev) => prev + 1);
    } else if (!allAnswersBrowsed) {
      fetchRandomAnswer();
    }
  }, [
    isBrowsing,
    startBrowsing,
    currentAnswerIndex,
    answers.length,
    allAnswersBrowsed,
    fetchRandomAnswer,
  ]);

  // Navigate to previous answer
  const goToPrevAnswer = useCallback(() => {
    if (!isBrowsing) return;
    if (currentAnswerIndex > 0) {
      setCurrentAnswerIndex((prev) => prev - 1);
    }
  }, [isBrowsing, currentAnswerIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!selectedTotem) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLInputElement
      ) {
        return;
      }

      // Don't handle if no answers
      if (selectedTotem.total_answers_count === 0) return;

      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        goToNextAnswer();
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        goToPrevAnswer();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedTotem, goToNextAnswer, goToPrevAnswer]);

  // Scroll-based navigation
  useEffect(() => {
    if (!selectedTotem) return;

    const handleWheel = (e: WheelEvent) => {
      // Prevent scroll in browsing mode
      if (isBrowsing) {
        e.preventDefault();
      }

      // Don't handle if no answers
      if (selectedTotem.total_answers_count === 0) return;

      const now = Date.now();
      if (now - lastScrollTime.current < 500) return;

      if (e.deltaY > 30) {
        lastScrollTime.current = now;
        goToNextAnswer();
      } else if (e.deltaY < -30) {
        lastScrollTime.current = now;
        goToPrevAnswer();
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [selectedTotem, isBrowsing, goToNextAnswer, goToPrevAnswer]);

  // Format relative time
  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    }
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading archive...</p>
      </div>
    );
  }

  // Show totem detail view
  if (selectedTotem) {
    const currentAnswer = answers[currentAnswerIndex];
    const hasAnswers = selectedTotem.total_answers_count > 0;
    const canGoNext =
      !allAnswersBrowsed || currentAnswerIndex < answers.length - 1;

    // Intro view (before browsing)
    if (!isBrowsing) {
      return (
        <div className={styles.introContainer}>
          <div className={styles.introContent}>
            <Link to="/archive" className={styles.backLink}>
              ← Back to archive
            </Link>

            <div className={styles.statsBar}>
              <div className={styles.stat}>
                <span className={styles.statValue}>
                  ${(selectedTotem.total_paid_amount_cents / 100).toFixed(2)}
                </span>
                <span className={styles.statLabel}>Prize Pool</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>
                  Closed {formatTimeAgo(selectedTotem.period_end)}
                </span>
                <span className={styles.statLabel}>Status</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>
                  {selectedTotem.total_answers_count}
                </span>
                <span className={styles.statLabel}>Answers</span>
              </div>
            </div>

            <TotemHead question={selectedTotem.question} />

            {/* Winner section */}
            {selectedTotem.winner_answer_id &&
              selectedTotem.winner_answer_content && (
                <div className={styles.winnerSection}>
                  <div className={styles.winnerBadge}>🏆 Winning Answer</div>
                  <div className={styles.winnerContent}>
                    <p>{selectedTotem.winner_answer_content}</p>
                  </div>
                </div>
              )}

            {!selectedTotem.winner_answer_id && (
              <div className={styles.noWinnerSection}>
                <p>No winner — pot rolled over to next totem</p>
              </div>
            )}

            <div className={styles.introNav}>
              {hasAnswers ? (
                <>
                  <button
                    className={styles.startBrowsingBtn}
                    onClick={startBrowsing}
                    disabled={loadingAnswers}
                  >
                    {loadingAnswers ? "Loading..." : "Browse All Answers"}
                  </button>
                  <p className={styles.navHint}>
                    <kbd>↓</kbd>/<kbd>J</kbd> next · <kbd>↑</kbd>/<kbd>K</kbd>{" "}
                    prev · or scroll
                  </p>
                </>
              ) : (
                <p className={styles.noAnswersYet}>
                  No answers were submitted to this totem.
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Browsing view
    return (
      <div className={styles.container}>
        <div className={styles.fixedHeader} ref={headerRef}>
          <div className={styles.fixedHeaderInner}>
            <Link to="/archive" className={styles.backLink}>
              ← Back to archive
            </Link>

            <div className={styles.statsBar}>
              <div className={styles.stat}>
                <span className={styles.statValue}>
                  ${(selectedTotem.total_paid_amount_cents / 100).toFixed(2)}
                </span>
                <span className={styles.statLabel}>Prize Pool</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>
                  Closed {formatTimeAgo(selectedTotem.period_end)}
                </span>
                <span className={styles.statLabel}>Status</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>
                  {selectedTotem.total_answers_count}
                </span>
                <span className={styles.statLabel}>Answers</span>
              </div>
            </div>

            <TotemHead question={selectedTotem.question} compact />
          </div>
        </div>

        {/* Spacer to push content below fixed header */}
        <div className={styles.headerSpacer} style={{ height: headerHeight }} />

        {/* Full-screen answer display */}
        <div className={styles.answersViewport}>
          <div className={styles.answerStack}>
            {currentAnswer && (
              <div
                key={currentAnswer.answer_id}
                className={styles.fullScreenAnswer}
              >
                <TotemSegment
                  answerId={currentAnswer.answer_id}
                  content={currentAnswer.content}
                  isNew={currentAnswerIndex === answers.length - 1}
                  canLike={false}
                  hideActions={true}
                  likeCount={currentAnswer.like_count}
                />
              </div>
            )}
          </div>
        </div>

        {/* Fixed navigation bar */}
        <div className={styles.fixedNavBar}>
          <span className={styles.answerCounter}>
            {currentAnswerIndex + 1} / {answers.length}
            {allAnswersBrowsed && " ✓"}
          </span>

          <span className={styles.keyboardHints}>
            <kbd>↓</kbd>/<kbd>J</kbd> next · <kbd>↑</kbd>/<kbd>K</kbd> prev
          </span>

          <div className={styles.navButtons}>
            <button
              className={styles.navBtn}
              onClick={goToPrevAnswer}
              disabled={currentAnswerIndex === 0}
              aria-label="Previous answer"
            >
              ↑
            </button>
            <button
              className={styles.navBtn}
              onClick={goToNextAnswer}
              disabled={!canGoNext || loadingAnswers}
              aria-label="Next answer"
            >
              {loadingAnswers ? "..." : "↓"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show archive list
  return (
    <div className={styles.listContainer}>
      <h1 className={styles.title}>Previous Totems</h1>
      <p className={styles.subtitle}>
        Explore the archive of past questions and answers.
      </p>

      {totems.length === 0 ? (
        <div className={styles.empty}>
          <p>No archived totems yet. Check back after the first cycle ends!</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {totems.map((totem) => (
            <Link
              key={totem.id}
              to={`/archive/${totem.id}`}
              className={styles.card}
            >
              <p className={styles.cardQuestion}>{totem.question}</p>
              <div className={styles.cardMeta}>
                <span className={styles.cardPrize}>
                  ${(totem.total_paid_amount_cents / 100).toFixed(2)}
                </span>
                <span className={styles.cardTime}>
                  {formatTimeAgo(totem.period_end)}
                </span>
              </div>
              {totem.has_winner && (
                <span className={styles.winnerTag}>🏆 Winner</span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
